<template>
  <div class="c-advanced-data-table">
    <v-layout v-bind="toolbarProps" class="px-4" wrap>
      <v-flex v-if="shownSearch" xs4>
        <c-search
          v-if="search"
          :label="searchLabel"
          @submit="updateSearch"
        />
        <c-advanced-search
          v-else-if="advancedSearch"
          :attributes="advancedSearchAttributes"
          :pending="advancedSearchLoading"
          @submit="updateAdvancedSearch"
          @reset="resetSearch"
        />
      </v-flex>
      <slot
        :selected="selected"
        :update-search="updateSearch"
        :update-advanced-search="updateAdvancedSearch"
        :reset-search="resetSearch"
        name="toolbar"
      />
      <v-flex
        v-if="hasMassActionsSlot"
        xs12
      >
        <v-expand-transition>
          <v-layout
            v-if="selected.length"
            class="px-2 mt-1"
          >
            <slot
              :selected="selected"
              :selected-keys="selectedKeys"
              :count="selected.length"
              :clear-selected="clearSelected"
              name="mass-actions"
            />
          </v-layout>
        </v-expand-transition>
      </v-flex>
    </v-layout>
    <v-data-table
      v-model="selected"
      ref="dataTable"
      :headers="preparedHeaders"
      :items="visibleItems"
      :loading="loading"
      :server-items-length="totalItems"
      :no-data-text="noDataText"
      :options="vDataTableOptions"
      :header-text="headerText"
      :footer-props="{ itemsPerPageOptions: itemsPerPageItems }"
      :item-key="itemKey"
      :show-select="selectAll"
      :show-expand="expand"
      :item-selectable="isItemSelectable"
      :hide-default-footer="hideActions || advancedPagination || noPagination"
      :class="tableClass"
      :dense="dense"
      :ultra-dense="ultraDense"
      :loader-height="loaderHeight"
      :ellipsis-headers="ellipsisHeaders"
      :expand-icon="expandIcon"
      checkbox-color="primary"
      @update:options="updateDataTableOptions"
    >
      <template v-if="hasHeaderSelectSlot" #header.data-table-select="props">
        <slot name="header.data-table-select" v-bind="props" />
      </template>

      <template v-if="hasItemSlot" #item="props">
        <slot name="item" v-bind="props" />
      </template>

      <template
        v-for="header in headers"
        #[getItemSlotName(header)]="props"
      >
        <slot
          :name="header.value"
          v-bind="getItemsProps(props)"
        >
          {{ props.item | get(header.value) }}
        </slot>
      </template>

      <template v-if="isExpandableItem" #item.data-table-expand="props">
        <v-icon
          v-if="checkIsExpandableItem(props.item)"
          :class="{ 'v-data-table__expand-icon--active': props.isExpanded }"
          class="v-data-table__expand-icon"
          @click.stop="props.expand(!props.isExpanded)"
        >
          {{ expandIcon }}
        </v-icon>
        <span v-else />
      </template>

      <template
        v-if="hasExpandSlot"
        #expanded-item="props"
      >
        <div
          v-if="checkIsExpandableItem(props.item)"
          class="secondary lighten-2"
        >
          <slot
            v-bind="props"
            name="expand"
          />
        </div>
      </template>

      <template #header="props">
        <slot
          name="header"
          v-bind="props"
        />
      </template>

      <template
        v-for="header in headerScopedSlots"
        #[header]="props"
      >
        <slot
          :name="header"
          v-bind="props"
        />
      </template>

      <template #progress="props">
        <slot
          name="progress"
          v-bind="props"
        />
      </template>

      <template #body.prepend="props">
        <slot
          name="body.prepend"
          v-bind="props"
        />
      </template>
    </v-data-table>

    <c-table-pagination
      v-if="!noPagination && options && advancedPagination"
      :total-items="totalItems"
      :items-per-page="options.itemsPerPage"
      :items="itemsPerPageItems"
      :page="options.page"
      @update:page="updatePage"
      @update:items-per-page="updateItemsPerPage"
      @input="updatePaginationOptions"
    />
  </div>
</template>

<script>
import { pick } from 'lodash';

import { mapIds } from '@/helpers/array';
import { prepareQueryWithAdvancedSearch, prepareQueryWithoutAdvancedSearch } from '@/helpers/search/advanced-search';

export default {
  model: {
    prop: 'selected',
    event: 'input',
  },
  props: {
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    itemsPerPageItems: {
      type: Array,
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectAll: {
      type: Boolean,
      default: false,
    },
    expand: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: false,
    },
    advancedSearch: {
      type: Boolean,
      default: false,
    },
    advancedPagination: {
      type: Boolean,
      default: false,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
    noPagination: {
      type: Boolean,
      default: false,
    },
    totalItems: {
      type: Number,
      required: false,
    },
    itemKey: {
      type: String,
      default: '_id',
    },
    noDataText: {
      type: String,
      required: false,
    },
    headerText: {
      type: String,
      default: 'text',
    },
    options: {
      type: Object,
      required: false,
    },
    isDisabledItem: {
      type: Function,
      default: item => !item,
    },
    isExpandableItem: {
      type: Function,
      required: false,
    },
    toolbarProps: {
      type: Object,
      required: false,
    },
    searchLabel: {
      type: String,
      default: '',
    },
    tableClass: {
      type: [String, Object],
      required: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    ultraDense: {
      type: Boolean,
      default: false,
    },
    ellipsisHeaders: {
      type: Boolean,
      default: false,
    },
    loaderHeight: {
      type: [String, Number],
      default: 2,
    },
    expandIcon: {
      type: String,
      default: '$expand',
    },
    advancedSearchAttributes: {
      type: Array,
      default: () => [],
    },
    advancedSearchLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedItems: [],
    };
  },
  computed: {
    vDataTableOptions() {
      return pick(this.options, ['page', 'itemsPerPage', 'sortBy', 'sortDesc', 'groupBy', 'groupDesc', 'mustSort', 'multiSort']);
    },

    preparedHeaders() {
      const headers = [];

      if (this.selectAll) {
        headers.push({
          value: 'data-table-select',
          text: '',
          sortable: false,
          width: '1px',
        });
      }

      if (this.expand) {
        headers.push({
          value: 'data-table-expand',
          text: '',
          sortable: false,
          width: '1px',
        });
      }

      return [
        ...headers,
        ...this.headers,
      ];
    },

    headerScopedSlots() {
      return Object.keys(this.$scopedSlots ?? {}).filter(name => name.startsWith('header.'));
    },

    selected: {
      get() {
        return this.selectedItems.filter(item => !this.isDisabledItem(item));
      },
      set(selected) {
        this.selectedItems = selected;
      },
    },

    selectedKeys() {
      return mapIds(this.selected, this.itemKey);
    },

    visibleItems() {
      return this.options?.itemsPerPage ? this.items.slice(0, this.options?.itemsPerPage) : this.items;
    },

    hasHeaderSelectSlot() {
      return this.$slots['header.data-table-select'] || this.$scopedSlots['header.data-table-select'];
    },

    hasItemSlot() {
      return this.$slots.item || this.$scopedSlots.item;
    },

    hasExpandSlot() {
      return this.$slots.expand || this.$scopedSlots.expand;
    },

    hasMassActionsSlot() {
      return this.$slots['mass-actions'] || this.$scopedSlots['mass-actions'];
    },

    shownSearch() {
      return this.search || this.advancedSearch;
    },
  },
  watch: {
    items(items) {
      if (this.selectAll) {
        const itemKeys = items.map(item => item[this.itemKey]);

        this.selectedItems = this.selectedItems.filter(selectedItem => itemKeys.includes(selectedItem[this.itemKey]));
      }
    },
  },
  methods: {
    checkIsExpandableItem(item) {
      return this.isExpandableItem ? this.isExpandableItem(item) : true;
    },

    updateOptions(options) {
      this.selected = [];

      this.$emit('update:options', options);
    },

    updateDataTableOptions(options) {
      return this.updateOptions({
        ...this.options,
        ...pick(options, ['page', 'itemsPerPage', 'sortBy', 'sortDesc', 'groupBy', 'groupDesc', 'mustSort', 'multiSort']),
      });
    },

    updateSearch(search) {
      this.updateOptions({ ...this.options, search, page: 1 });
    },

    updateAdvancedSearch(search) {
      this.updateOptions(prepareQueryWithAdvancedSearch(this.options, search));
    },

    resetSearch() {
      this.updateOptions(prepareQueryWithoutAdvancedSearch(this.options));
    },

    updateItemsPerPage(itemsPerPage) {
      this.updateOptions({ ...this.options, itemsPerPage });
    },

    updatePage(page) {
      this.updateOptions({ ...this.options, page });
    },

    updatePaginationOptions({ page, itemsPerPage }) {
      this.updateOptions({ ...this.options, page, itemsPerPage });
    },

    clearSelected() {
      this.selectedItems = [];
    },

    getItemsProps(state) {
      return {
        index: state.index,
        item: state.item,
        selected: state.selected,
        disabled: this.isDisabledItem(state.item),
        expanded: state.expanded,
        select: value => state.selected = value || !state.selected,
        expand: state.expand,
      };
    },

    getItemSlotName(header) {
      return `item.${header.value}`;
    },

    isItemSelectable(item) {
      return !this.isDisabledItem(item);
    },
  },
};
</script>

<style lang="scss" scoped>
.c-advanced-data-table {
  ::v-deep thead th {
    vertical-align: middle;
  }

  & .c-checkbox-wrapper {
    display: inline-flex;
  }
}
</style>
